import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaEye, FaComment, FaUserPlus, FaCalendar, 
  FaHeart, FaCheck, FaStar, FaSpinner, FaSyncAlt,
  FaFilter, FaSearch, FaTimes, FaUserCheck,
  FaUserTimes, FaMapMarkerAlt, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import profileService from '../../services/profile';
import favoriteService from '../../services/favorite';
import toast from 'react-hot-toast';
import Loader from '../common/Loader';
import './dashboard.css';

const Visits = () => {
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOnline, setFilterOnline] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [likingId, setLikingId] = useState(null);
  const [favoritingId, setFavoritingId] = useState(null);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const observerRef = useRef();
  
  const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || 'https://murima.julesferry-ga.com/uploads';

  // Charger les visiteurs
  const loadVisitors = useCallback(async (reset = false) => {
    if (!user?.id) return;

    if (reset) {
      setPage(1);
      setVisitors([]);
      setHasMore(true);
    }

    const loadingState = reset ? setLoading : setLoadingMore;
    loadingState(true);

    try {
      const response = await api.get('/get-visitors.php', {
        params: { 
          user_id: user.id,
          page: reset ? 1 : page,
          limit: 20,
          _t: Date.now()
        }
      });

      if (response.data.success) {
        const newVisitors = response.data.visitors || [];
        setVisitors(prev => reset ? newVisitors : [...prev, ...newVisitors]);
        setHasMore(response.data.hasMore || false);
        setTotal(response.data.total || 0);
        
        if (!reset) {
          setPage(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('❌ Error loading visitors:', error);
      toast.error('Erreur lors du chargement des visiteurs');
    } finally {
      loadingState(false);
    }
  }, [user?.id, page]);

  useEffect(() => {
    loadVisitors(true);
  }, [loadVisitors]);

  // Infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loadingMore && !loading) {
        loadVisitors();
      }
    }, options);

    const sentinel = document.getElementById('sentinel');
    if (sentinel) observerRef.current.observe(sentinel);

    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, loadVisitors]);

  // Filtrer les visiteurs
  useEffect(() => {
    let result = [...visitors];

    if (searchTerm) {
      result = result.filter(v => 
        v.pseudo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.ville?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterOnline) {
      result = result.filter(v => v.en_ligne);
    }

    setFilteredVisitors(result);
  }, [visitors, searchTerm, filterOnline]);

  // Rafraîchir
  const refreshVisitors = () => {
    loadVisitors(true);
    toast.success('Liste actualisée');
  };

  // Liker un visiteur
  const handleLike = async (visitorId, e) => {
    e.stopPropagation();
    
    setLikingId(visitorId);
    try {
      const result = await profileService.likeUser(visitorId);
      
      if (result.success) {
        setVisitors(prev =>
          prev.map(v =>
            v.id === visitorId
              ? { 
                  ...v, 
                  already_liked: true,
                  is_match: result.match || v.is_match
                }
              : v
          )
        );
        
        if (result.match) {
          toast.success(`C'est un match ! ❤️`, { duration: 5000 });
        } else {
          toast.success('Like envoyé !');
        }
      } else if (result.error === 'already_liked') {
        toast('Vous avez déjà liké ce profil', { icon: '💔' });
      } else {
        toast.error(result.error || 'Erreur lors du like');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setLikingId(null);
    }
  };

  // Ajouter/Retirer des favoris
  const handleFavorite = async (visitorId, isCurrentlyFavorite, e) => {
    e.stopPropagation();
    
    if (!user?.id) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    setFavoritingId(visitorId);
    try {
      if (isCurrentlyFavorite) {
        // Retirer des favoris
        const result = await favoriteService.removeFavorite(user.id, visitorId);
        if (result.success) {
          setVisitors(prev =>
            prev.map(v =>
              v.id === visitorId
                ? { ...v, is_favorite: false }
                : v
            )
          );
          toast.success('Retiré des favoris');
        } else {
          toast.error(result.error || 'Erreur lors du retrait');
        }
      } else {
        // Ajouter aux favoris
        const result = await favoriteService.addFavorite(user.id, visitorId);
        if (result.success) {
          setVisitors(prev =>
            prev.map(v =>
              v.id === visitorId
                ? { ...v, is_favorite: true }
                : v
            )
          );
          toast.success('Ajouté aux favoris ⭐');
        } else {
          if (result.error === 'already_favorite') {
            toast('Déjà dans vos favoris', { icon: '⭐' });
          } else {
            toast.error(result.error || 'Erreur lors de l\'ajout');
          }
        }
      }
    } catch (error) {
      console.error('❌ Favorite error:', error);
      toast.error('Erreur réseau');
    } finally {
      setFavoritingId(null);
    }
  };

  // Formateurs
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60));
    
    if (diff < 1) return 'À l\'instant';
    if (diff < 60) return `Il y a ${diff} min`;
    if (diff < 120) return 'Il y a 1h';
    if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
    if (diff < 2880) return 'Hier';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (avatar.startsWith('http')) return avatar;
    return `${UPLOADS_URL}/${avatar}`;
  };

  const activeFiltersCount = filterOnline ? 1 : 0;

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="visits-page">
      {/* Header */}
      <div className="visits-header">
        <div className="header-title">
          <h1>
            <FaEye className="header-icon" />
            Mes visiteurs
            <span className="header-count">{total}</span>
          </h1>
          <p className="header-subtitle">
            Les personnes qui ont consulté votre profil
          </p>
        </div>

        <button 
          className="btn-refresh"
          onClick={refreshVisitors}
          disabled={loading}
          title="Actualiser"
        >
          <FaSyncAlt className={loading ? 'spin' : ''} />
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="visits-filters">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par nom ou ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="search-clear"
              onClick={() => setSearchTerm('')}
            >
              <FaTimes />
            </button>
          )}
        </div>

        <button 
          className={`filters-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter />
          <span>Filtres</span>
          {activeFiltersCount > 0 && (
            <span className="filters-badge">{activeFiltersCount}</span>
          )}
        </button>
      </div>

      {/* Panneau de filtres */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-option">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filterOnline}
                onChange={(e) => setFilterOnline(e.target.checked)}
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-label">
                <span className="online-dot"></span>
                En ligne uniquement
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Liste des visiteurs */}
      {filteredVisitors.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon-wrapper">
            <FaEye className="empty-icon" />
          </div>
          <h3>Aucune visite</h3>
          <p>
            {searchTerm || filterOnline
              ? 'Aucun résultat ne correspond à vos filtres'
              : 'Personne n\'a encore visité votre profil'
            }
          </p>
          {(searchTerm || filterOnline) && (
            <button 
              className="btn-primary"
              onClick={() => {
                setSearchTerm('');
                setFilterOnline(false);
              }}
            >
              Effacer les filtres
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="visitors-grid">
            {filteredVisitors.map(visitor => (
              <div 
                key={visitor.id} 
                className="visitor-card"
                onClick={() => navigate(`/profile/${visitor.id}`)}
              >
                <div className="visitor-header">
                  <div className="visitor-avatar">
                    <img 
                      src={getAvatarUrl(visitor.avatar)} 
                      alt={visitor.pseudo}
                      loading="lazy"
                    />
                    {visitor.en_ligne && (
                      <span className="online-indicator" title="En ligne">
                        <span className="pulse"></span>
                      </span>
                    )}
                  </div>

                  <div className="visitor-badges">
                    {visitor.is_verified && (
                      <span className="badge-verified" title="Profil vérifié">
                        <FaCheck />
                      </span>
                    )}
                    {visitor.is_premium && (
                      <span className="badge-premium" title="Membre premium">
                        <FaStar />
                      </span>
                    )}
                    {visitor.is_match && (
                      <span className="badge-match" title="Match !">
                        ❤️
                      </span>
                    )}
                    {visitor.is_favorite && (
                      <span className="badge-favorite" title="Dans vos favoris">
                        ⭐
                      </span>
                    )}
                  </div>
                </div>

                <div className="visitor-info">
                  <h3 className="visitor-name">
                    {visitor.pseudo}, <span className="visitor-age">{visitor.age}</span>
                  </h3>
                  
                  <p className="visitor-location">
                    <FaMapMarkerAlt className="location-icon" />
                    {visitor.ville || 'Ville inconnue'}
                  </p>

                  <p className="visitor-date">
                    <FaCalendar className="date-icon" />
                    {formatDate(visitor.date_visite)}
                  </p>
                </div>

                <div className="visitor-actions">
                  <button 
                    className={`btn-like ${visitor.already_liked ? 'liked' : ''}`}
                    onClick={(e) => handleLike(visitor.id, e)}
                    disabled={likingId === visitor.id || visitor.already_liked}
                    title={visitor.already_liked ? 'Déjà liké' : 'Liker'}
                  >
                    {likingId === visitor.id ? (
                      <FaSpinner className="spinner" />
                    ) : (
                      <FaHeart />
                    )}
                  </button>

                  <button 
                    className="btn-message"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/messages?user=${visitor.id}`);
                    }}
                    title="Envoyer un message"
                  >
                    <FaComment />
                  </button>

                  <button 
                    className={`btn-add ${visitor.is_favorite ? 'favorited' : ''}`}
                    onClick={(e) => handleFavorite(visitor.id, visitor.is_favorite, e)}
                    disabled={favoritingId === visitor.id}
                    title={visitor.is_favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  >
                    {favoritingId === visitor.id ? (
                      <FaSpinner className="spinner" />
                    ) : (
                      <FaUserPlus />
                    )}
                  </button>
                </div>

                {visitor.is_match && (
                  <div className="match-badge" title="Match !">
                    <span className="match-text">Match !</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Infinite scroll sentinel */}
          <div id="sentinel" className="sentinel">
            {loadingMore && (
              <div className="loading-more">
                <FaSpinner className="spinner" /> Chargement...
              </div>
            )}
            {!hasMore && visitors.length > 0 && (
              <div className="no-more">
                <p>Plus aucun visiteur à charger</p>
              </div>
            )}
          </div>

          {/* Résumé */}
          <div className="visits-summary">
            <p>
              <strong>{total}</strong> visite{total > 1 ? 'urs' : 'eur'} au total
              {filteredVisitors.length < total && (
                <> (<strong>{filteredVisitors.length}</strong> affiché{filteredVisitors.length > 1 ? 's' : ''})</>
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default Visits;